import {
  Col,
  Card,
  CardBody,
  Container,
  Row,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import { Btn } from "../../AbstractElements";
import { Epin, Income, LevelIncomeReport, pinOrderReport } from "../../utils/Constant";
import Breadcrumbs from "../../CommonElements/Breadcrumbs/Breadcrumbs";
import DatePicker from "react-datepicker";
import { useEffect, useState } from "react";
import { decryptData } from "../../utils/helper/Crypto";
import * as XLSX from "xlsx";
import { format } from "date-fns";
import { IoMdPrint } from "react-icons/io";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import DataTable from "react-data-table-component";
import { useCommonService } from "../../Service/CommonService/Commonservice";
import { useSweetAlert } from "../../Context/SweetAlertContext";
import Datanotfound from "../PageNotFound/Datanotfound";
import CustomPagination from "../CustomPagination";
import { IoSearchSharp } from "react-icons/io5";
import TableToolbar from "../../CommonElements/ToolBarModule/ToolBarModule";

const PinOrderComponent = () => {
  const { showAlert } = useSweetAlert();
  const { ApiCalling } = useCommonService();

  const [data, setData] = useState<any[]>([]);
  const [columns, setColumns] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  });
  const [toDate, setToDate] = useState(new Date());
  const [filterText, setFilterText] = useState("");

  const [memberID, setMemberID] = useState(
    decryptData(localStorage.getItem("clientId") as string)
  );

  // For modal order details
  const [orderDetails, setOrderDetails] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalFilterText, setModalFilterText] = useState("");
  const [modalCurrentPage, setModalCurrentPage] = useState(1);
  const [modalPerPage, setModalPerPage] = useState(10);

  const filteredModalData = orderDetails.filter((row) =>
    Object.values(row).some((val) =>
      String(val).toLowerCase().includes(modalFilterText.toLowerCase())
    )
  );

  const paginatedModalData = filteredModalData.slice(
    (modalCurrentPage - 1) * modalPerPage,
    modalCurrentPage * modalPerPage
  );

  useEffect(() => {
    setModalCurrentPage(1);
  }, [modalFilterText, orderDetails, isModalOpen]);

  useEffect(() => {
    setMemberID(decryptData(localStorage.getItem("clientId") as string));
  }, []);

  // ✅ Fetch Report
  const FetchData = async (
    page = currentPage,
    size = perPage,
    search = filterText
  ) => {
    setLoading(true);
    const formattedFromDate = format(fromDate, "dd-MMMM-yyyy");
    const formattedToDate = format(toDate, "dd-MMMM-yyyy");

    const obj = {
      MemberId: memberID,
      FromDate: formattedFromDate,
      ToDate: formattedToDate,
      Page: page,
      Size: size,
      Search: search,
      ActionMode: "GetAllRecordByMember",
    };

    const param = {
      procName: "GetPinTransferReport",
      Para: JSON.stringify(obj),
    };

    try {
      const res = await ApiCalling(param);

      if (Array.isArray(res) && res.length > 0) {
        const cleanedData = res.map(({ TotalCount, ...rest }) => rest);
        const keys = Object.keys(cleanedData[0] || {});

        const list = keys.map((col) => ({
          name: col,
          selector: (row: any) => row[col],
          sortable: true,
          cell: (row: any) => (
            <div dangerouslySetInnerHTML={{ __html: row[col] }} />
          ),
        }));

        setColumns(list);
        setData(cleanedData);
        setTotalRows(res[0]?.TotalCount ?? 0);
      } else {
        setColumns([]);
        setData([]);
        setTotalRows(0);
      }
    } catch (err) {
      console.error("Data fetch failed:", err);
    }

    setLoading(false);
  };

  // ✅ Fetch order details for eye button
  const ShowOrder = async (orderId: number) => {
    setIsModalOpen(true); // open modal immediately
    setModalLoading(true); // show loading spinner
    setOrderDetails([]); // clear previous data

    try {
      const formattedFromDate = format(fromDate, "dd-MMMM-yyyy");
      const formattedToDate = format(toDate, "dd-MMMM-yyyy");

      const obj = {
        MemberId: memberID,
        FromDate: formattedFromDate,
        ToDate: formattedToDate,
        OrderId: orderId,
        ActionMode: "SelectByIdViewByMember",
      };

      const param = {
        procName: "GetPinTransferReport",
        Para: JSON.stringify(obj),
      };

      const res = await ApiCalling(param);

      if (Array.isArray(res)) {
        setOrderDetails(res);
      } else {
        showAlert("Oops!", "No details found.");
      }
    } catch (err) {
      console.error("Failed to load order details:", err);
    } finally {
      setModalLoading(false); // stop loading
    }
  };

  // Expose globally so SQL onclick works
  (window as any).ShowOrder = ShowOrder;

  // ✅ Submit
  const handleSubmit = (e: any) => {
    e.preventDefault();
    setCurrentPage(1);
    FetchData(1, perPage, filterText);
  };

  useEffect(() => {
    FetchData(currentPage, perPage, filterText);
  }, [currentPage, perPage, filterText]);

  // ✅ Data cleaning for export
  const cleanDataForExport = (data: any[]) => {
    return data.map((row) => {
      const cleanedRow: any = {};
      for (let key in row) {
        if (typeof row[key] === "string") {
          const div = document.createElement("div");
          div.innerHTML = row[key];
          cleanedRow[key] = div.textContent || "";
        } else {
          cleanedRow[key] = row[key];
        }
      }
      return cleanedRow;
    });
  };

  const exportToExcel = (data: any) => {
    if (data.length === 0) {
      showAlert("Oops!", "No data available.");
      return;
    }
    const cleaned = cleanDataForExport(data);
    const ws = XLSX.utils.json_to_sheet(cleaned);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, "TableData.xlsx");
  };

  const exportAndPrintCSV = (data: any) => {
    if (data.length === 0) {
      showAlert("Oops!", "No data available at the moment.");
      return;
    }

    const cleanedData = cleanDataForExport(data);
    const csvData = XLSX.utils.sheet_to_csv(
      XLSX.utils.json_to_sheet(cleanedData)
    );

    printCSVData(csvData);
  };

  const printCSVData = (csvData: string) => {
    const escapeHtml = (text: string) =>
      text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

    const rows = csvData
      .split("\n")
      .map((row) => row.split(","))
      .filter(
        (row) => row.length > 1 && row.some((cell) => cell.trim() !== "")
      );

    if (rows.length === 0) {
      alert("No valid data to print.");
      return;
    }

    const printWindow = window.open("", "", "height=900,width=1200");
    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(`
      <html>
      <head>
        <title>Printable Report</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
          h2 { text-align: center; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }
          th { background-color: #f2f2f2; font-weight: bold; }
          td { white-space: pre-wrap; word-break: break-word; }
          @media print { body { margin: 10mm; } button { display: none; } }
        </style>
      </head>
      <body>
        <h2>Report</h2>
        <table>
          <thead>
            <tr>${rows[0]
              .map((cell) => `<th>${escapeHtml(cell.trim())}</th>`)
              .join("")}</tr>
          </thead>
          <tbody>
            ${rows
              .slice(1)
              .map(
                (row) =>
                  `<tr>${row
                    .map((cell) => `<td>${escapeHtml(cell.trim())}</td>`)
                    .join("")}</tr>`
              )
              .join("")}
          </tbody>
        </table>
        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() { window.close(); };
          };
        </script>
      </body>
      </html>
    `);
      printWindow.document.close();
      printWindow.focus();
    }
  };

  return (
    <div className="page-body">
      <Breadcrumbs
        mainTitle={pinOrderReport}
        parent={Epin}
        ChildName={pinOrderReport}
      />

      <Container fluid className="innerPageCard">
        {/* 🔍 Search */}
        <form onSubmit={handleSubmit}>
          <Row>
            <Col md="3">
              <FormGroup>
                <Label>From Date:</Label>
                <DatePicker
                  className="form-control"
                  selected={fromDate}
                  onChange={(d: any) => setFromDate(d)}
                  dateFormat="dd-MMMM-yyyy"
                />
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup>
                <Label>To Date:</Label>
                <DatePicker
                  className="form-control"
                  selected={toDate}
                  onChange={(d: any) => setToDate(d)}
                  dateFormat="dd-MMMM-yyyy"
                />
              </FormGroup>
            </Col>
            <Col md="3" className="d-flex align-items-center">
              <button type="submit" className="btn btn-primary mt-3">
                <IoSearchSharp /> Search
              </button>
            </Col>
          </Row>
        </form>

        {/* 🔍 Search + Export Buttons */}
        <Row className="mt-3 align-items-center">
          {/* <Col className="d-flex gap-2">
            <Btn
              color="primary"
              onClick={() => exportAndPrintCSV(data)}
              disabled={data.length === 0}
            >
              <IoMdPrint size={20} /> Print
            </Btn>

            <Btn
              color="primary"
              onClick={() => exportToExcel(data)}
              disabled={data.length === 0}
            >
              <PiMicrosoftExcelLogoFill size={20} /> Excel
            </Btn>
          </Col> */}

          <Col md="3" className="ms-auto">
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder="Search..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
              />
              {/* <button
                className="btn btn-primary rounded-pill mx-3"
                onClick={() => {
                  setCurrentPage(1);
                  FetchData(1, perPage, filterText);
                }}
              >
                <IoSearchSharp /> Search
              </button> */}
            </div>
          </Col>
        </Row>

        {/* 📊 Table */}
        <Card className="mt-3">
          <CardBody>
            <DataTable
              data={data}
              columns={columns}
              highlightOnHover
              pagination={false}
              noDataComponent={<Datanotfound />}
            />

            {data.length > 0 && (
              <CustomPagination
                currentPage={currentPage}
                perPage={perPage}
                totalRows={totalRows}
                onPageChange={(page) => setCurrentPage(page)}
                onRowsPerPageChange={(size) => {
                  setPerPage(size);
                  setCurrentPage(1);
                }}
              />
            )}
          </CardBody>
        </Card>
      </Container>

      <Modal
        isOpen={isModalOpen}
        toggle={() => setIsModalOpen(!isModalOpen)}
        size="lg"
        centered
        className="custom-modal"
      >
        <ModalHeader
          toggle={() => setIsModalOpen(false)}
          className="bg-primary text-white"
        >
          <h5 className="mb-0">📦 Order Details</h5>
        </ModalHeader>

        <ModalBody>
          {modalLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading details...</p>
            </div>
          ) : orderDetails.length > 0 ? (
            <>
              {/* Toolbar with search, print, excel */}
              <TableToolbar
                data={orderDetails} // Pass full order details here
                filterText={modalFilterText}
                setFilterText={setModalFilterText}
                onSearch={() => {}} // input already updates modalFilterText
                onExportExcel={() => exportToExcel(filteredModalData)}
                onPrint={() => exportAndPrintCSV(filteredModalData)}
              />

              {/* Table with filtered data */}
              <div className="table-responsive mt-2">
                <table className="table table-striped table-hover align-middle">
                  <thead className="table-dark">
                    <tr>
                      {Object.keys(filteredModalData[0] || {}).map((k) => (
                        <th key={k} className="text-nowrap">
                          {k}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedModalData.map((row, i) => (
                      <tr key={i}>
                        {Object.keys(row).map((k) => (
                          <td
                            key={k}
                            dangerouslySetInnerHTML={{ __html: row[k] }}
                          />
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredModalData.length > modalPerPage && (
                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <button
                      className="btn btn-secondary"
                      disabled={modalCurrentPage === 1}
                      onClick={() => setModalCurrentPage(modalCurrentPage - 1)}
                    >
                      ◀ Previous
                    </button>
                    <span>
                      Page {modalCurrentPage} of{" "}
                      {Math.ceil(filteredModalData.length / modalPerPage)}
                    </span>
                    <button
                      className="btn btn-secondary"
                      disabled={
                        modalCurrentPage ===
                        Math.ceil(filteredModalData.length / modalPerPage)
                      }
                      onClick={() => setModalCurrentPage(modalCurrentPage + 1)}
                    >
                      Next ▶
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-3">
              <p className="text-muted mb-0">No details found.</p>
            </div>
          )}
        </ModalBody>

        {/* 🔽 Inline CSS for modal */}
        <style>{`
    .custom-modal .modal-content {
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 6px 18px rgba(0,0,0,0.25);
    }

    .custom-modal .modal-header {
      padding: 12px 20px;
    }

    .custom-modal .modal-body {
      max-height: 70vh;
      overflow-y: auto;
    }

    .custom-modal table {
      font-size: 14px;
    }

    .custom-modal th, .custom-modal td {
      vertical-align: middle;
    }
  `}</style>
      </Modal>
    </div>
  );
};

export default PinOrderComponent;
